import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScrollToTop from '../components/ScrollToTop';
import Home from '../pages/Home';
import Workwear from '../pages/Workwear';
import Product from '../pages/Product';
import Contact from '../pages/Contact';
import Privacy from '../pages/Privacy';
import Terms from '../pages/Terms';
import Faqs from '../pages/Faqs';
import Login from '../pages/Login';
import Register from '../pages/Register';
import TrackOrder from '../pages/TrackYourOrder';
import AboutUs from '../pages/AboutUs';
import MyAccount from '../pages/MyAccount';
import Cart from '../pages/Cart';
import Checkout from '../pages/Checkout';
import AdminLayout from '../components/Admin/AdminLayout';
import AdminLogin from '../pages/Admin/AdminLogin';
import AdminDashboard from '../pages/Admin/Dashboard';
import AdminProducts from '../pages/Admin/Products';
import AdminOrders from '../pages/Admin/Orders';
import AdminOrderDetails from '../pages/Admin/OrderDetails';
import AdminCustomers from '../pages/Admin/Customers';
import AdminAnalytics from '../pages/Admin/Analytics';
import Payment from '../pages/Admin/Activity';
import AdminNotFound from '../pages/Admin/AdminNotFound';
import Inventory from '../pages/Admin/Inventory';
import Reports from '../pages/Admin/Reports';
import Coupons from '../pages/Admin/Coupons';
import ChatManager from '../pages/Admin/ChatManager';
import KnowledgeBase from '../pages/Admin/KnowledgeBase';
import Settings from '../pages/Admin/Settings';
import Help from '../pages/Admin/Help';
import Paint from '../pages/Paint';
import PaintCategory from '../pages/PaintCategory';
import PaintSubcategory from '../pages/PaintSubcategory';
import WorkwearSubcategory from '../pages/WorkwearSubcategory';
import Profile from '../pages/Profile';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import MyOrders from '../pages/MyOrders';
import SearchPage from '../pages/SearchPage';

const AppRouter = () => {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const languageMatch = path.match(/^\/([a-z]{2})(?:\/|$)/);
    const detectedLang = languageMatch ? languageMatch[1] : 'en';

    if (i18n.language !== detectedLang) {
      i18n.changeLanguage(detectedLang);
      localStorage.setItem('i18nextLng', detectedLang);
    }
  }, [location.pathname, i18n]);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/:lang" element={<Home />} />
        <Route path="/:lang/about-us" element={<AboutUs />} />
        <Route path="/:lang/workwear" element={<Workwear />} />
        <Route
          path="/:lang/workwear/:subcategory"
          element={<WorkwearSubcategory />}
        />
        <Route path="/:lang/paint" element={<Paint />} />
        <Route path="/:lang/collection" element={<Paint />} />
        <Route
          path="/:lang/paint/:subcategory"
          element={<PaintSubcategory />}
        />
        <Route path="/:lang/product" element={<Product />} />
        <Route path="/:lang/product/:id" element={<Product />} />
        <Route path="/:lang/contact" element={<Contact />} />
        <Route path="/:lang/privacy" element={<Privacy />} />
        <Route path="/:lang/terms" element={<Terms />} />
        <Route path="/:lang/faqs" element={<Faqs />} />
        <Route path=":lang/login" element={<Login />} />
        <Route path=":lang/register" element={<Register />} />
        <Route path=":lang/profile" element={<Profile />} />
        <Route path=":lang/orders" element={<MyOrders />} />
        <Route path=":lang/forgot-password" element={<ForgotPassword />} />
        <Route path=":lang/reset-password/:token" element={<ResetPassword />} />
        <Route path="/:lang/track-your-order" element={<TrackOrder />} />
        <Route path="/:lang/account" element={<MyAccount />} />
        <Route path="/:lang/cart" element={<Cart />} />
        <Route path="/:lang/checkout" element={<Checkout />} />
        <Route path="/:lang/cart" element={<Cart />} />
        <Route path="/:lang/checkout" element={<Checkout />} />
        <Route path="/:lang/paint-category" element={<PaintCategory />} />
        <Route path="/:lang/search" element={<SearchPage />} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:id" element={<AdminOrderDetails />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="activity" element={<Payment />} />
          <Route path="reports" element={<Reports />} />
          <Route path="coupons" element={<Coupons />} />
          <Route path="chat" element={<ChatManager />} />
          <Route path="knowledge" element={<KnowledgeBase />} />
          <Route path="settings" element={<Settings />} />
          <Route path="help" element={<Help />} />
          <Route path="*" element={<AdminNotFound />} />
        </Route>

        <Route
          path="/"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}`}
              replace
            />
          }
        />
        <Route
          path="/about-us"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/about-us`}
              replace
            />
          }
        />
        <Route
          path="/workwear"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/workwear`}
              replace
            />
          }
        />
        <Route
          path="/paint"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/paint`}
              replace
            />
          }
        />
        <Route
          path="/collection"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/collection`}
              replace
            />
          }
        />
        <Route
          path="/contact"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/contact`}
              replace
            />
          }
        />
        <Route
          path="/privacy"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/privacy`}
              replace
            />
          }
        />
        <Route
          path="/terms"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/terms`}
              replace
            />
          }
        />
        <Route
          path="/faqs"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/faqs`}
              replace
            />
          }
        />
        <Route
          path="/login"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/login`}
              replace
            />
          }
        />
        <Route
          path="/register"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/register`}
              replace
            />
          }
        />
        <Route
          path="/profile"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/profile`}
              replace
            />
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/forgot-password`}
              replace
            />
          }
        />
        <Route
          path="/reset-password/:token"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/reset-password/:token`}
              replace
            />
          }
        />
        <Route
          path="/track-your-order"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'
                }/track-your-order`}
              replace
            />
          }
        />
        <Route
          path="/account"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/account`}
              replace
            />
          }
        />
        <Route
          path="/cart"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/cart`}
              replace
            />
          }
        />
        <Route
          path="/paint-category"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/paint-category`}
              replace
            />
          }
        />
        <Route
          path="/search"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/search`}
              replace
            />
          }
        />

        <Route
          path="*"
          element={
            <Navigate
              to={`/${localStorage.getItem('i18nextLng') || 'en'}`}
              replace
            />
          }
        />
      </Routes>
    </>
  );
};

export default AppRouter;
